<template>
  <v-card
    class="legend"
    flat
    tile
  >
    <template v-for="layer in config.layers">
      <h5
        :key="layer.dataset.href"
        class="font-weight-bold"
        style="word-break:normal;line-height:1"
      >
        <v-row align="center">
          <v-col
            :cols="1"
            class="pb-1"
          >
            <v-btn
              icon
              small
              @click="$store.commit('toggle', layer.dataset.id)"
            >
              <v-icon small>
                mdi-eye{{ layers && layers[layer.dataset.id] && layers[layer.dataset.id].visible ? '' : '-off' }}
              </v-icon>
            </v-btn>
          </v-col>
          <v-col class="pb-1">
            {{ layer.title || layer.dataset.title }}
          </v-col>
        </v-row>
      </h5>
      <v-row
        v-if="datasets[layer.dataset.id]"
        :key="'i-'+layer.dataset.href"
        class="mb-0"
      >
        <v-col
          :cols="1"
          class="text-right pr-2"
        >
          <v-divider vertical />
        </v-col>
        <v-col>
          <div
            v-if="layer.shapes.type === 'icon-single'"
            style="width:100%"
          >
            <v-icon>
              mdi-{{ layer.shapes.icon.name }}
            </v-icon>
            &nbsp;Icône unique
          </div>
          <div v-else-if="layer.shapes.type === 'icon-multiple'">
            <v-subheader
              style="height:36px; line-height:1"
              class="px-0"
            >
              {{ fieldLabel(layer.dataset.id, layer.shapes.field) }}
            </v-subheader>
            <ul>
              <li
                v-for="(item, i) in layer.shapes.icons"
                :key="i"
                style="cursor:pointer"
                @click="toggleFilter(layer.dataset.id, layer.shapes.field, item.value)"
              >
                <v-icon :color="layers && layers[layer.dataset.id] && layers[layer.dataset.id].filters && layers[layer.dataset.id].filters[layer.shapes.field] === item.value ? 'primary' : ''">
                  mdi-{{ item.icon.name }}
                </v-icon>
                &nbsp;{{ formattedValue(item.value, datasets[layer.dataset.id], layer.shapes.field) }}
              </li>
            </ul>
          </div>
          <div
            v-else-if="layer.shapes.type === 'shape'"
            style="width:100%"
          >
            <v-icon>
              mdi-vector-polygon-variant
            </v-icon>
            &nbsp;Géométries
          </div>
          <div
            v-else-if="layer.shapes.type === 'circle'"
            style="width:100%"
          >
            <v-row>
              <v-col
                :cols="5"
                class="pt-4"
              >
                <svg
                  width="64"
                  height="64"
                  viewBox="0 0 64 64"
                >

                  <circle
                    cx="32"
                    cy="32"
                    r="30"
                    stroke="black"
                    stroke-width="2"
                    stroke-dasharray="5,5"
                    fill="none"
                  />
                  <circle
                    v-if="layer.shapes.radius.type === 'variable'"
                    cx="32"
                    cy="48"
                    r="13"
                    stroke="black"
                    stroke-width="2"
                    stroke-dasharray="5,5"
                    fill="none"
                  />
                </svg>
              </v-col>
              <v-col
                v-if="layer.shapes.radius.type === 'variable'"
                :cols="7"
              >
                <div>{{ datasets[layer.dataset.id] && datasets[layer.dataset.id].maxValueCircle.toLocaleString('fr') }}</div>
                <div>
                  &nbsp;
                <!-- <v-divider
                    vertical
                    style="height:20px"
                  /> -->
                </div>
                <div>{{ datasets[layer.dataset.id] && datasets[layer.dataset.id].minValueCircle.toLocaleString('fr') }}</div>
              </v-col>
            </v-row>
          </div>
          <!-- </v-col>
        <v-col> -->
          <div v-if="layer.colors.type === 'single'">
            <v-icon :color="layer.colors.value">
              mdi-square
            </v-icon>
            &nbsp;Couleur unique
          </div>
          <div v-else-if="layer.colors.type === 'field'">
            Couleur venant des données
          </div>
          <div v-else>
            <v-subheader
              style="height:36px; line-height:1"
              class="px-0"
            >
              {{ fieldLabel(layer.dataset.id, (layer.colors.rangeOptions || layer.colors.enumOptions).field) }}
            </v-subheader>
            <ul>
              <li
                v-for="(item, i) in colors(layer.colors, datasets[layer.dataset.id].minValueColor, datasets[layer.dataset.id].maxValueColor, datasets[layer.dataset.id].quantiles)"
                :key="i"
                style="cursor:pointer;"
                @click="toggleFilter(layer.dataset.id, (layer.colors.rangeOptions || layer.colors.enumOptions).field, item.value)"
              >
                <v-icon
                  :color="item.color"
                  :style="layers && layers[layer.dataset.id] && layers[layer.dataset.id].filters && layers[layer.dataset.id].filters[(layer.colors.rangeOptions || layer.colors.enumOptions).field] === item.value ? 'background-color:black;' : ''"
                >
                  mdi-square
                </v-icon>
                &nbsp;{{ layer.colors.enumOptions ? formattedValue(item.value, datasets[layer.dataset.id], layer.colors.enumOptions.field) : item.value }}
              </li>
            </ul>
          </div>
        </v-col>
      </v-row>
    </template>
  </v-card>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import colors from '../../assets/colors'
import dayjs from 'dayjs'

export default {
  computed: {
    ...mapState(['layers', 'datasets']),
    ...mapGetters(['config'])
  },
  methods: {
    colors,
    fieldLabel (datasetId, fieldId) {
      if (!this.datasets[datasetId]) return fieldId
      const field = this.datasets[datasetId].schema.find(f => f.key === fieldId)
      if (!field) return fieldId
      return field.title || field['x-originalName'] || field.key
    },
    formattedValue (value, dataset, fieldId) {
      const field = dataset.schema.find(f => f.key === fieldId)
      if (!field) return fieldId
      if (field['x-labels']) return field['x-labels'][value]
      if (field.type === 'number' || field.type === 'integer') return value.toLocaleString()
      if (field['x-refersTo'] === 'http://schema.org/Date') return dayjs(value).format('DD/MM/YYYY')
      return value
    },
    toggleFilter (id, field, value) {
      const filters = Object.assign({}, this.layers[id].filters)
      if (value === filters[field]) delete filters[field]
      else filters[field] = value
      this.$store.commit('filter', { id, filters })
    }
  }
}
</script>

<style>
.v-card.legend ul {
  list-style-type: none;
  padding-left: 0px;
  margin-top: 0;
  margin-bottom: 0px;
}
</style>
